import Service, { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import dayjs from 'dayjs';
import { task, timeout } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import ENV from 'lumen/config/environment';

const VERSION_POLL_INTERVAL = macroCondition(isTesting()) ? 1 : 300000;
const LAST_REFRESH_KEY = 'lastRefresh';

export default class VersionService extends Service {
  @service declare errorTracking: Services['error-tracking'];
  @service declare localStorage: Services['local-storage'];

  versionPoll = null;

  /**
   * Checks the current version with the latest version
   * This is fetched from the gcloud bucket
   */
  versionPollEventTask = task({ drop: true }, async (): Promise<void> => {
    const lastRefresh = dayjs.utc(this.localStorage.getItem(LAST_REFRESH_KEY));
    const lastRefreshMinsAgo = dayjs.utc().diff(lastRefresh, 'minute');

    this.errorTracking.addBreadcrumb({ message: 'checking version...' });
    this.errorTracking.addBreadcrumb({
      message: `Current time is: ${dayjs.utc().format()} `,
    });
    this.errorTracking.addBreadcrumb({
      message: `Last refresh time is: ${this.localStorage.getItem(LAST_REFRESH_KEY)}`,
    });
    this.errorTracking.addBreadcrumb({
      message: `It's been ${lastRefreshMinsAgo} minutes since the last check`,
    });

    // Don't check if it has been less than 5 minutes since the last refresh
    if (lastRefreshMinsAgo > 5) {
      try {
        const versionUrl = formatUrl(`${ENV.staticURL}version.json`, {
          v: dayjs().format(),
        });
        const response = await fetch(versionUrl);

        if (!response.ok) {
          throw new Error(`Fetching version.json failed: ${response.status}`);
        }

        const result = (await response.json()) as { version: string };

        this.errorTracking.addBreadcrumb({
          message: `current version is: ${this.currentVersion}`,
        });
        this.errorTracking.addBreadcrumb({
          message: `Latest version is: ${result.version}`,
        });

        if (result.version !== this.currentVersion) {
          this.localStorage.setItem(LAST_REFRESH_KEY, dayjs.utc().toString());
          VersionService.reloadApp();
        }
      } catch (error) {
        this.errorTracking.addBreadcrumb({
          message: 'checking version failed',
        });
        this.errorTracking.addBreadcrumb({
          message: error as string,
        });
      }
    }

    // Timout at the end of the task so the polling event is done instantly, but all follow-up calls have to wait until the timeout is finished
    await timeout(VERSION_POLL_INTERVAL);
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentVersion() {
    // get commit hash from version
    return ENV['ember-cli-app-version'].version;
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // store the current timestamp in localstorage
    this.localStorage.setItem(LAST_REFRESH_KEY, dayjs.utc().format());
  }

  willDestroy(...args: Parameters<Service['willDestroy']>): void {
    super.willDestroy(...args);

    // Stop polling events, should any still be running
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.versionPollEventTask.cancelAll();
  }

  /**
   * Starts a poll event
   * This is debounced and will only be executed once every five minutes (max)
   */

  manualPoll(): void {
    if (macroCondition(isTesting())) {
      return;
    }

    this.errorTracking.addBreadcrumb({ message: 'Manual version poll' });
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.versionPollEventTask.perform();
  }

  static reloadApp(): void {
    window.location.reload();
  }
}

declare module '@ember/service' {
  interface Registry {
    version: VersionService;
  }
}
